import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import DialPhone, { PhoneUserIdentity } from '../../connection/phone';
import { SendDtmf, HangupPhone, HangupPhoneByUser } from '../../connection/phone';
import { GreenroomState } from '../../stores/types/types';

type DialStatus = 'idle' | 'dialing' | 'connected' | 'error';

interface Props {
	identity: PhoneUserIdentity & { first_name?: string; last_name?: string; phone?: string };
	onClose: () => void;
	/** Called when the dial-out API call succeeds. Receives the SIP connectionId for the new call. */
	onCallStarted?: (sipConnectionId: string) => void;
	/** Called when the user clicks End Call from within the dialer. */
	onHangup?: () => void;
	/** True when the SIP stream is confirmed active in Redux (hasActivePhoneCall). */
	isCallConnected?: boolean;
	/** The sipConnectionId once the call has started, passed down from parent state. */
	activeSipConnectionId?: string | null;
}

const PAD_ROWS = [
	[{ key: '1', sub: '' }, { key: '2', sub: 'ABC' }, { key: '3', sub: 'DEF' }],
	[{ key: '4', sub: 'GHI' }, { key: '5', sub: 'JKL' }, { key: '6', sub: 'MNO' }],
	[{ key: '7', sub: 'PQRS' }, { key: '8', sub: 'TUV' }, { key: '9', sub: 'WXYZ' }],
	[{ key: '*', sub: '' }, { key: '0', sub: '+' }, { key: '#', sub: '' }],
];

export default function FeedPhoneDialer({ identity, onClose, onCallStarted, onHangup, isCallConnected, activeSipConnectionId }: Props) {
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meetingId = useSelector(
		(state: GreenroomState) => state.SessionReducer.meeting?.meeting_id,
	);

	// Look up the mask phone directly — bypasses the use_profile_masks display
	// gate in useProfile so the number populates regardless of that feature flag.
	const activeMasks = useSelector(
		(state: GreenroomState) => state.MasksReducer.activeMasksEntity?.entityValue,
	);
	const profileMasks = useSelector((state: GreenroomState) => state.MasksReducer.profileMasks);
	const resolveMaskPhone = () => {
		if (!identity.email) return undefined;
		const maskId = activeMasks?.[identity.email];
		return maskId != null ? profileMasks[maskId]?.phone ?? undefined : undefined;
	};

	const [phoneNumber, setPhoneNumber] = useState(() => resolveMaskPhone() || identity.phone || '');
	// Initialize directly to connected if a call is already active when the dialer opens (e.g. after minimize).
	const [status, setStatus] = useState<DialStatus>(() => isCallConnected ? 'connected' : 'idle');
	const [errorMessage, setErrorMessage] = useState('');
	const touched = useRef(false);

	const displayName =
		[identity.first_name, identity.last_name].filter(Boolean).join(' ') ||
		identity.email ||
		'Unknown';

	const initials = (
		[identity.first_name?.[0], identity.last_name?.[0]].filter(Boolean).join('') ||
		identity.email?.[0] ||
		'?'
	).toUpperCase();

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [onClose]);

	// Populate from mask data whenever Redux updates.
	useEffect(() => {
		if (touched.current) return;
		const phone = resolveMaskPhone() || identity.phone || '';
		if (phone) setPhoneNumber(phone);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeMasks, profileMasks, identity.email, identity.phone]);

	// Transition to connected once the SIP stream is confirmed.
	useEffect(() => {
		if (isCallConnected && (status === 'dialing' || status === 'idle')) {
			setStatus('connected');
		}
	}, [isCallConnected, status]);

	const appendDigit = useCallback((digit: string) => {
		touched.current = true;
		setPhoneNumber((prev) => prev + digit);
	}, []);

	const backspace = useCallback(() => {
		touched.current = true;
		setPhoneNumber((prev) => prev.slice(0, -1));
	}, []);

	const handleDial = async () => {
		if (!phoneNumber.trim() || !token || !meetingId || status === 'dialing') return;
		setStatus('dialing');
		setErrorMessage('');
		const result = await DialPhone(token, phoneNumber.trim(), meetingId, identity);
		if (result?.message) {
			setStatus('error');
			setErrorMessage(result.message);
		} else {
			onCallStarted?.(result?.sipCall?.connectionId ?? '');
			// Stay open — transition to connected happens via isCallConnected prop effect.
		}
	};

	const handleDtmfKey = useCallback(async (digit: string) => {
		if (!token || !meetingId || !activeSipConnectionId) return;
		await SendDtmf(token, meetingId, activeSipConnectionId, digit);
	}, [token, meetingId, activeSipConnectionId]);

	const handleEndCall = async () => {
		if (!token || !meetingId) return;
		let result: { ok: boolean; message?: string };
		if (activeSipConnectionId) {
			result = await HangupPhone(token, meetingId, activeSipConnectionId);
		} else if (identity.gr_user) {
			// Fallback: sipConnectionId was lost (e.g. component remounted) — look up by user.
			result = await HangupPhoneByUser(token, meetingId, identity.gr_user);
		} else {
			setStatus('error');
			setErrorMessage('Cannot end call: connection ID unavailable. Refresh the page if the call persists.');
			return;
		}
		if (!result.ok) {
			setStatus('error');
			setErrorMessage(result.message ?? 'Failed to end call');
			return;
		}
		onHangup?.();
		onClose();
	};

	const isConnected = status === 'connected';

	return ReactDOM.createPortal(
		<div className="phone-dialer-modal-backdrop" onClick={onClose}>
			<div className="phone-dialer-modal" onClick={(e) => e.stopPropagation()}>

				{/* User info */}
				<div className="phone-dialer-modal__header">
					<div className="phone-dialer-modal__avatar">
						{identity.poster
							? <img src={identity.poster} alt={displayName} />
							: <span>{initials}</span>
						}
					</div>
					<div className="phone-dialer-modal__user-info">
						<span className="phone-dialer-modal__name">{displayName}</span>
						{isConnected
							? <span className="phone-dialer-modal__status phone-dialer-modal__status--connected">Connected</span>
							: status === 'dialing'
								? <span className="phone-dialer-modal__status phone-dialer-modal__status--connecting">Connecting…</span>
								: identity.email && <span className="phone-dialer-modal__email">{identity.email}</span>
						}
					</div>
				</div>

				{/* Number display — hidden in connected DTMF mode */}
				{!isConnected && (
					<div className="phone-dialer-modal__screen">
						<input
							className="phone-dialer-modal__number-input"
							type="tel"
							placeholder="+1 555 000 0000"
							value={phoneNumber}
							onChange={(e) => { touched.current = true; setPhoneNumber(e.target.value); }}
							disabled={status === 'dialing'}
							autoFocus
						/>
						{phoneNumber && (
							<button
								className="phone-dialer-modal__backspace"
								onClick={backspace}
								type="button"
								tabIndex={-1}
							>
								⌫
							</button>
						)}
					</div>
				)}

				{/* Number pad */}
				<div className="phone-dialer-modal__pad">
					{PAD_ROWS.map((row, i) => (
						<div key={i} className="phone-dialer-modal__pad-row">
							{row.map(({ key, sub }) => (
								<button
									key={key}
									className="phone-dialer-modal__pad-key"
									onClick={() => isConnected ? handleDtmfKey(key) : appendDigit(key)}
									type="button"
									tabIndex={-1}
								>
									<span className="phone-dialer-modal__pad-digit">{key}</span>
									{sub && <span className="phone-dialer-modal__pad-sub">{sub}</span>}
								</button>
							))}
						</div>
					))}
				</div>

				{status === 'error' && (
					<span className="phone-dialer-modal__error">{errorMessage}</span>
				)}

				{/* Actions */}
				<div className="phone-dialer-modal__actions">
					{isConnected ? (
						<>
							<button className="phone-dialer-modal__cancel" onClick={onClose} type="button">
								Minimize
							</button>
							<button className="phone-dialer-modal__hangup" onClick={handleEndCall} type="button">
								End Call
							</button>
						</>
					) : (
						<>
							<button className="phone-dialer-modal__cancel" onClick={onClose} type="button">
								Cancel
							</button>
							<button
								className="phone-dialer-modal__call"
								onClick={handleDial}
								disabled={status === 'dialing' || !phoneNumber.trim()}
								type="button"
							>
								{status === 'dialing' ? 'Calling…' : 'Call'}
							</button>
						</>
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
}
